import api from "../../../api/Base";

// Third-parties.
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import React from "react";
import dayjs from "dayjs";
import ReactTooltip from "react-tooltip";

// Context
import { InstanceContext } from "../../../context/InstanceContext";
import { FilterContext } from "../../../context/FilterContext";
import { HostContext } from "../../../context/HostContext";

// Types
import { ActivityComparisonResultsTableProps } from "./types";
import { InstanceOptions } from "../statistics-comparison/types";
import InstanceTargetHost from '../../../types/instance/InstanceTargetHost';
import HostTarget from "../../../types/host/HostTarget";

// Helpers
import Helper from "../../../helpers/Helper";
import { archiverUrl, fetchResults, fetchWithAuthorization, getAggregateInt, getTimeZone } from "../../../helpers/utils";
import ConditionalRender from "../../../helpers/ConditionalRender";

const ActivityComparisonResultsTable = (props: ActivityComparisonResultsTableProps) => {
    const { instances } = useContext(InstanceContext);
    const { hosts } = useContext(HostContext);
    const { waitTimes, instanceOptions, options, generateId } = props

    const [loading, setLoading] = useState(false)

    const [totalExecutions, setTotalExecutions] = useState<any[]>([])
    const [matchedHosts, setMatchedHosts] = useState<HostTarget[][]>([[]]);
    const [hostAverageCPU, setHostAverageCPU] = useState<any[]>([])
    const [hostAverageMemory, setHostAverageMemory] = useState<any[]>([])

    const [eventData, setEventData] = useState({
        detectedChanges: [0, 0],
        registeredEvents: [0, 0],
        registeredAlerts: [0, 0]
    });


    const [cpuCounts, setCpuCounts] = useState<any[]>([])
    const { filters } = useContext(FilterContext);

    const timezone = getTimeZone()
    ReactTooltip.rebuild();

    const resetData = () => {
        props.setWaitTimes([])
        setTotalExecutions([])
        setMatchedHosts([])
        setHostAverageCPU([])
        setHostAverageMemory([])
        setEventData({
            detectedChanges: [0, 0],
            registeredEvents: [0, 0],
            registeredAlerts: [0, 0]
        })
        setCpuCounts([])
    }

    useEffect(() => {
        setLoading(true)
        resetData()
        Promise.all([getTotalTime(), getTotalExecutions(), getMatchedHosts(), getCpuCounts(), getEvents()]).then(() => {
            setLoading(false)
        })
    }, [generateId])

    const aggregateInt = useMemo(() => getAggregateInt(options.aggregate, instanceOptions, instances), [options.aggregate])

    const getPeriodHeaders = useCallback(() => {
        return instanceOptions.map((instanceOption: InstanceOptions, key: number) => {
            if (instanceOption.periodStart && instanceOption.periodEnd) {
                return <th key={key} className="col-3 text-end" scope="col">
                    {Helper.getFormattedPeriodHeadingFromDate(instanceOption.periodStart, instanceOption.periodEnd)}
                    <br/> {getFilterName(instanceOption.snapshotId)}
                </th>
            }
            return null
        })
    }, [generateId])

    const getFilterName = (snapshotId: number | undefined) => {
        const findFilter = filters.find(item => item.id === snapshotId)
        return findFilter ? ` (${findFilter.name})` : ''
    }

    const getFormattedDate = (instanceDate: Date) => {
        return dayjs(instanceDate).format('YYYY-MM-DD+HH:mm');
    };

    const getTotalExecutions = useCallback(async() => {
        const promises: Promise<Response>[] = []
        instanceOptions.forEach((instanceOption: InstanceOptions) => {
            const type = instances.find(i => i.id === instanceOption.instanceId)?.type
            promises.push(fetchWithAuthorization(`${archiverUrl(1)}/statistic/summary?from=${getFormattedDate(instanceOption.periodStart)}&to=${getFormattedDate(instanceOption.periodEnd)}&tz=${timezone}&id=${instanceOption.instanceId}&interval=${aggregateInt}&type=${type}&statistic=executions`))
        })

        const results = await fetchResults(promises, false)
        const total: any[] = []
        results.forEach((result: any[]) => {
            let sum = 0;
            try {
                sum = result.reduce((accumulator, resultItem) => {
                    // @ts-ignore
                    return accumulator + resultItem.sum;
                }, 0);
            } catch (ignore) {

            }
            total.push(sum)
        })
        setTotalExecutions(total)
    }, [generateId])

    const getTotalTime = useCallback(async() => {
        const promises: Promise<Response>[] = []
        instanceOptions.forEach((instanceOption: InstanceOptions) => {
            const type = instances.find(i => i.id === instanceOption.instanceId)?.type
            promises.push(fetchWithAuthorization(`${archiverUrl(1)}/activity/summary?from=${getFormattedDate(instanceOption.periodStart)}&to=${getFormattedDate(instanceOption.periodEnd)}&tz=${timezone}&id=${instanceOption.instanceId}&interval=${aggregateInt}&type=${type}`))
        })

        const results = await fetchResults(promises, false)
        const total: any[] = []
        results.forEach((result: any[]) => {
            let sum = 0;
            try {
                sum = result.reduce((accumulator, resultItem) => {
                    // @ts-ignore
                    return accumulator + resultItem.waittime;
                }, 0);
            } catch (ignore) {

            }
            total.push(sum)
        })
        props.setWaitTimes(total)
    }, [generateId])


    const getEvents = useCallback(async() => {
        const promises: Promise<Response>[] = []
        const hasInterimChanges = Helper.showInterimChanges(instanceOptions)

        // Push main instance options fetch requests
        instanceOptions.forEach((instanceOption: InstanceOptions) => {
            promises.push(fetchWithAuthorization(`${archiverUrl(2)}/event?from=${getFormattedDate(instanceOption.periodStart)}&to=${getFormattedDate(instanceOption.periodEnd)}&tz=${timezone}&id=${instanceOption.instanceId}&interval=${aggregateInt}&datasourceid=${instanceOption.instanceId}&sort=timeslice+desc&limit=50000`))
        });
        if (hasInterimChanges) {
            // Push interim changes fetch requests
            promises.push(fetchWithAuthorization(`${archiverUrl(2)}/event?from=${getFormattedDate(instanceOptions[0].periodEnd)}&to=${getFormattedDate(instanceOptions[1].periodStart)}&tz=${timezone}&interval=${aggregateInt}&datasourceid=${instanceOptions[0].instanceId}&sort=timeslice+desc&limit=50000`))
            promises.push(fetchWithAuthorization(`${archiverUrl(2)}/event?from=${getFormattedDate(instanceOptions[0].periodEnd)}&to=${getFormattedDate(instanceOptions[1].periodStart)}&tz=${timezone}&interval=${aggregateInt}&datasourceid=${instanceOptions[1].instanceId}&sort=timeslice+desc&limit=50000`))
        }

        const results = await fetchResults(promises);
        const firstInstanceEvents = results[0].filter((e: any) => e.event === 'custom');
        const firstInstanceChanges = results[0].filter((e: any) => e.event === 'change');
        const firstInstanceAlerts = results[0].filter((e: any) => e.event === 'alert');
        const secondInstanceEvents = results[1].filter((e: any) => e.event === 'custom');
        const secondInstanceChanges = results[1].filter((e: any) => e.event === 'change');
        const secondInstanceAlerts = results[1].filter((e: any) => e.event === 'alert');

        setEventData({
            detectedChanges: [firstInstanceChanges.length, secondInstanceChanges.length],
            registeredEvents: [firstInstanceEvents.length, secondInstanceEvents.length],
            registeredAlerts: [firstInstanceAlerts.length, secondInstanceAlerts.length]
        });

        // Combine results[2] and results[3] into a single array (interim changes)
        const combinedResults = hasInterimChanges ? [...results[2], ...results[3]] : []

        props.setCombinedChanges([results[0], results[1], combinedResults]);

    }, [generateId]);

    const getCpuCounts = useCallback(async() => {
        const promises: Promise<Response>[] = []
        instanceOptions.forEach((instanceOption: InstanceOptions) => {
            promises.push(fetchWithAuthorization(`${archiverUrl(1)}/statistic/summary?from=${getFormattedDate(instanceOption.periodStart)}&to=${getFormattedDate(instanceOption.periodEnd)}&tz=${timezone}&id=${instanceOption.instanceId}&interval=${aggregateInt}&statistic=cpucount`))
        })

        const results = await fetchResults(promises)
        const cpuCountResponses = results.map(i => i?.[0] || 0).map(i => i.avg)
        setCpuCounts(cpuCountResponses)
    }, [generateId])

    const getMatchedHosts = useCallback(async() => {
        const promises: Promise<Response>[] = [];

        // Loop through each instance and fetch hosts
        instanceOptions.forEach((instanceOption: InstanceOptions) => {
            promises.push(fetchWithAuthorization(`${archiverUrl(1)}/datasource/host?id=${instanceOption.instanceId}`));
        });

        // Fetch all results
        const results = await fetchResults(promises);

        // Process the results to filter matching hosts
        const matchedHostResponses = results.map((response: any) => {
            const instanceHosts: HostTarget[] = [];
            if (response.length) {
                response.forEach((hostData: InstanceTargetHost) => {
                    const matchedHosts = hosts.filter(host => host.id === hostData.hostid);
                    if (matchedHosts.length === 1) {
                        instanceHosts.push(matchedHosts[0]);
                    }
                });
            }

            return instanceHosts;
        });
        setMatchedHosts(matchedHostResponses);
    }, [generateId])

    const averageExecutions = useMemo(() => {
        return totalExecutions.map((executions: any, index: number) => {
            return executions && waitTimes[index] ? parseFloat((waitTimes[index] / executions).toString()) : 0
        })
    }, [waitTimes, totalExecutions])

    const getDifferenceText = (values: any, formatFn: Function) => {
        if (values[0] !== undefined && values[1] !== undefined) {
            const percent = Helper.getFormattedNumber(Helper.getPercentageDifference(values[1], values[0]));
            const diff = formatFn(Math.abs(values[0] - values[1]));
            const hidePercent = values[0] === 0 || values[1] === 0
            return `${diff} ${hidePercent ? '' : `(${percent}%)`}`;
        }
        return '';
    };

    const timeDifference = useMemo(() => getDifferenceText(waitTimes, Helper.getTimeInEnglish), [waitTimes]);

    const executionsDifference = useMemo(() => getDifferenceText(totalExecutions, Helper.getFormattedNumber), [totalExecutions]);

    const averageExecutionsDifference = useMemo(() => getDifferenceText(averageExecutions, Helper.getTimeInEnglish), [averageExecutions]);

    const averageHostCPUDifference = useMemo(() => {
        if (!hostAverageCPU[1] || !hostAverageCPU[0]) {
            return ''
        }
        const diff = hostAverageCPU.length ? Helper.getFormattedNumber(hostAverageCPU[1] - hostAverageCPU[0]) : ''
        return diff ? `${diff}%` : ''
    }, [hostAverageCPU]);

    const averageHostMemoryDifference = useMemo(() => {
        if (!hostAverageMemory[1] || !hostAverageMemory[0]) {
            return ''
        }
        const diff = hostAverageCPU.length ? Helper.getFormattedNumber(hostAverageMemory[1] - hostAverageMemory[0]) : ''
        return diff ? `${diff}%` : ''
    }, [hostAverageMemory]);

    const eventsDifference = useMemo(() => getDifferenceText(eventData.registeredEvents, Helper.getFormattedNumber), [eventData.registeredEvents]);

    const changesDifference = useMemo(() => getDifferenceText(eventData.detectedChanges, Helper.getFormattedNumber), [eventData.detectedChanges]);

    const alertsDifference = useMemo(() => getDifferenceText(eventData.registeredAlerts, Helper.getFormattedNumber), [eventData.registeredAlerts]);

    const cpuDifference = useMemo(() => getDifferenceText(cpuCounts, Helper.getFormattedNumber), [cpuCounts]);

    const getInstanceRows = useCallback(() => {
        return instanceOptions.map((instanceOption: InstanceOptions, key: number) => {
            if (instanceOption.instanceName) {
                return <td key={key} className="text-end">
                    {instanceOption.instanceName}
                </td>
            }
            return null
        })
    }, [generateId])

    const getTotalTimeRows = useCallback(() => {
        return instanceOptions.map((_instanceOption: InstanceOptions, index: number) => {
            return <td key={index} className="text-end">
                {waitTimes[index] ? Helper.getTimeInEnglish(waitTimes[index]) : '-'}
            </td>
        })
    }, [waitTimes])

    const getTotalExecutionsRows = useCallback(() => {
        return instanceOptions.map((_instanceOption: InstanceOptions, index: number) => {
            return <td key={index} className="text-end">
                {totalExecutions[index] ? Helper.getFormattedNumber(totalExecutions[index]) : '-'}
            </td>
        })
    }, [totalExecutions])

    const getAverageExecutionRows = useCallback(() => {
        return instanceOptions.map((_instanceOption: InstanceOptions, index: number) => {
            return <td key={index} className="text-end">
                {averageExecutions[index] ? Helper.getTimeInEnglish(averageExecutions[index]) : '-'}
            </td>
        })
    }, [averageExecutions])

    const getCpuCountRows = useCallback(() => {
        return instanceOptions.map((_instanceOption: InstanceOptions, index: number) => {
            return <td key={index} className="text-end">
                {cpuCounts[index] ? cpuCounts[index] : '-'}
            </td>
        })
    }, [cpuCounts])

    const getHostsAverageCPURows = useCallback(() => {
        return instanceOptions.map((instanceOption: InstanceOptions, key: number) => {
            const hostsLength = matchedHosts[key] ? matchedHosts[key].length : 0
            return <td key={key}>
                <div className='className="text-end d-flex justify-content-between align-items-center'>

                    {/*show tooltip only if hosts exist*/}
                    {hostsLength ? <span className="badge bg-info tip mt-1"
                                         data-tip={`${instanceOption.instanceName} has ${Helper.getFormattedBadgeCount(hostsLength)} associated host${((hostsLength) ? 's' : '')}`}>{Helper.getFormattedBadgeCount(hostsLength)}</span> :
                        <span/>}
                    <span className='text-end'>{(!hostAverageCPU[key] ? '-' : `${hostAverageCPU[key]}%`)}</span>
                </div>
            </td>
        })
    }, [hostAverageCPU])

    const getHostsAverageMemoryRows = useCallback(() => {
        return instanceOptions.map((instanceOption: InstanceOptions, key: number) => {
            const hostsLength = matchedHosts[key] ? matchedHosts[key].length : 0
            return <td key={key}>
                <div className='className="text-end d-flex justify-content-between align-items-center'>

                    {/*show tooltip only if hosts exist*/}
                    {hostsLength ? <span className="badge bg-info tip mt-1"
                                         data-tip={`${instanceOption.instanceName} has ${Helper.getFormattedBadgeCount(hostsLength)} associated host${((hostsLength) ? 's' : '')}`}>{Helper.getFormattedBadgeCount(hostsLength)}</span> :
                        <span/>}
                    <span>{(!hostAverageMemory[key] ? '-' : `${hostAverageMemory[key]}%`)}</span>
                </div>
            </td>
        })
    }, [hostAverageMemory])

    useEffect(() => {
        const fetchHostStatistics = async() => {
            const avgProcessor: number[] = [];
            const avgMemory: number[] = [];

            await Promise.all(
                matchedHosts.map(async(host: HostTarget[], key: number) => {
                    if (!host.length) {
                        avgProcessor[key] = 0;
                        avgMemory[key] = 0;
                        return;
                    }

                    const cpuUtilisation: number[] = [];
                    const memoryUtilisation: number[] = [];

                    await Promise.all(
                        host.map(async(item) => {
                            const cpuResponse = await api.get(
                                `host/statistic/summary?from=${getFormattedDate(instanceOptions[key].periodStart)}&to=${getFormattedDate(instanceOptions[key].periodEnd)}&tz=${timezone}&interval=${aggregateInt}&statistic=cpuutilisation&id=${item.id}`
                            );
                            if (cpuResponse.data.length > 0) {
                                cpuUtilisation.push(Math.round(cpuResponse.data[0].avg));
                            }

                            const memoryResponse = await api.get(
                                `host/statistic/summary?from=${getFormattedDate(instanceOptions[key].periodStart)}&to=${getFormattedDate(instanceOptions[key].periodEnd)}&tz=${timezone}&interval=${aggregateInt}&statistic=memoryutilisation&id=${item.id}`
                            );
                            if (memoryResponse.data.length > 0) {
                                memoryUtilisation.push(Math.round(memoryResponse.data[0].avg));
                            }
                        })
                    );

                    avgProcessor[key] = parseInt(
                        Helper.getAverageFromArray(cpuUtilisation).toFixed(0)
                    );
                    avgMemory[key] = parseInt(
                        Helper.getAverageFromArray(memoryUtilisation).toFixed(0)
                    );
                })
            );

            setHostAverageCPU(avgProcessor);
            setHostAverageMemory(avgMemory);
            ReactTooltip.rebuild();
        };

        void fetchHostStatistics();
    }, [matchedHosts]);

    return <React.Fragment>
        <div className="card collapsible">
            <div className="card-header">
                <i className="fal fa-stopwatch fa-fw" aria-hidden="true"/>
                Activity Comparison Results
                <i className="collapse-toggle" role="button" data-bs-toggle="collapse"
                   data-bs-target="#collapseResults"
                   aria-expanded="false" aria-controls="collapseResults"/>
            </div>
            <div id="collapseResults" className="card-body collapse show">
                <ConditionalRender if={loading}>
                    <div className="loader"
                         style={{
                             borderWidth: 0,
                             position: "absolute",
                             width: '80%',
                             margin: 'auto',
                             height: "5px"
                         }}>
                        <div className="bar"/>
                    </div>
                </ConditionalRender>
                <div className="row row-cols-lg-12 mt-3">
                    <table className="table stats-comparison-summary">
                        <thead>
                        <tr>
                            <th className="col-3" scope="col">Metric</th>
                            {getPeriodHeaders()}
                            <th className="col-3 text-end" scope="col">Difference</th>
                        </tr>
                        </thead>
                        <tbody>

                        <tr>
                            <td>Instance Name</td>
                            {getInstanceRows()}
                            <td className="text-end"></td>
                        </tr>
                        <tr>
                            <td>Total DB Time</td>
                            {getTotalTimeRows()}
                            <td className="text-end">
                                {timeDifference || '-'}
                                {timeDifference &&
                                    <i className={Helper.getChangeIcon(waitTimes[0], waitTimes[1])}/>}
                            </td>
                        </tr>
                        <tr>
                            <td>Total Executions</td>
                            {getTotalExecutionsRows()}
                            <td className="text-end">
                                {executionsDifference || '-'}
                                {executionsDifference &&
                                    <i className={Helper.getChangeIcon(totalExecutions[0], totalExecutions[1])}/>}
                            </td>
                        </tr>
                        <tr>
                            <td>Average Execution Time</td>
                            {getAverageExecutionRows()}
                            <td className="text-end">
                                {averageExecutionsDifference || '-'}
                                {averageExecutionsDifference &&
                                    <i className={Helper.getChangeIcon(averageExecutions[0], averageExecutions[1])}/>}
                            </td>
                        </tr>
                        <tr>
                            <td>Host Average CPU</td>
                            {getHostsAverageCPURows()}
                            <td className="text-end">
                                {averageHostCPUDifference || '-'}
                                {averageHostCPUDifference &&
                                    <i className={Helper.getChangeIcon(hostAverageCPU[0], hostAverageCPU[1])}/>}
                            </td>
                        </tr>
                        <tr>
                            <td>Host Average Memory</td>
                            {getHostsAverageMemoryRows()}
                            <td className="text-end">
                                {averageHostMemoryDifference || '-'}
                                {averageHostMemoryDifference &&
                                    <i className={Helper.getChangeIcon(hostAverageMemory[0], hostAverageMemory[1])}/>}
                            </td>
                        </tr>
                        <tr>
                            <td>Detected Changes
                                <sup> <i className="fal fa-exchange fa-fw"
                                         aria-hidden="true"></i></sup></td>
                            <td className="text-end">{eventData.detectedChanges[0]}</td>
                            <td className="text-end">{eventData.detectedChanges[1]}</td>
                            <td className="text-end">
                                {changesDifference}
                                {changesDifference &&
                                    <i className={Helper.getChangeIcon(eventData.detectedChanges[0], eventData.detectedChanges[1])}/>}
                            </td>
                        </tr>
                        <tr>
                            <td>Custom Events
                                <sup> <i className="fal fa-calendar fa-fw"
                                         aria-hidden="true"></i></sup></td>
                            <td className="text-end">{eventData.registeredEvents[0]}</td>
                            <td className="text-end">{eventData.registeredEvents[1]}</td>
                            <td className="text-end">
                                {eventsDifference}
                                {eventsDifference &&
                                    <i className={Helper.getChangeIcon(eventData.registeredEvents[0], eventData.registeredEvents[1])}/>}
                            </td>
                        </tr>
                        <tr>
                            <td>Alerts
                                <sup> <i className="fal fa-exclamation-triangle red-icon fa-fw"
                                         aria-hidden="true"></i></sup></td>
                            <td className="text-end">{eventData.registeredAlerts[0]}</td>
                            <td className="text-end">{eventData.registeredAlerts[1]}</td>
                            <td className="text-end">
                                {alertsDifference}
                                {alertsDifference &&
                                    <i className={Helper.getChangeIcon(eventData.registeredAlerts[0], eventData.registeredAlerts[1])}/>}
                            </td>
                        </tr>
                        <tr>
                            <td>CPU Count</td>
                            {getCpuCountRows()}
                            <td className="text-end">
                                {cpuDifference || '-'}
                                {cpuDifference && <i className={Helper.getChangeIcon(cpuCounts[0], cpuCounts[1])}/>}
                            </td>
                        </tr>

                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    </React.Fragment>
}

export default ActivityComparisonResultsTable;
